import { useToast } from '@quadminds/mindkit';
import { IAuthMeV2OutputDto } from '@quadminds/shared/auth-system';
import { FetchNextPageOptions, InfiniteData, InfiniteQueryObserverResult, useQueryClient } from '@tanstack/react-query';
import { AxiosProgressEvent } from 'axios';
import _ from 'lodash';
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useDeleteDocument } from '../../hooks/useDeleteDocument';
import { useDeleteFolder } from '../../hooks/useDeleteFolder';
import useGetDocuments from '../../hooks/useGetDocuments';
import useReplaceFile from '../../hooks/useReplaceFile';
import { useUpdateDocument } from '../../hooks/useUpdateDocument';
import { useUpdateFolder } from '../../hooks/useUpdateFolder';
import { useUploadKnoledgeFile } from '../../hooks/useUploadKnowledgeFile';
import { Document } from '../../interfaces/document/document.input';
import { Folder, Paginated } from '../../interfaces/paginated.interface';
import { agentsApi } from '../../services/agents-api.service';
import { FileItem, uploadRequestStatusType, UploadDestination, ViewType } from '../../types/File/File.types';

type KnowledgeCRUDActionsType = {
    pages: FileItem[];
    statictics: {
        folders: number;
        files: {
            total: number;
        };
    };
    paths: { id: string; name: string }[];
    hasNextPage: boolean;
    isLoadingDocuments: boolean;
    isFetchingDocuments: boolean;
    isLoadingMorePages: boolean;
    isLoadingDelete: boolean;
    isErrorLoadingDocuments: boolean;
    isLoadingUpdateDocument: boolean;
    isLoadingUploadFile: boolean;
    documentFilters?: Record<string, string>;
    rejectedUploads: PromiseRejectedResult[];
    clearRejectedUploadByFileName: (fileName: string) => void;
    handleViewType: (viewType: ViewType) => void;
    handleCurrentPath: (path: string) => void;
    fetchNextPage: (
        options?: FetchNextPageOptions,
    ) => Promise<InfiniteQueryObserverResult<InfiniteData<Paginated<FileItem, Folder>>, Error>>;
    handleDialogState: (dialog: keyof KnowledgeContextType['dialogs'], isOpen: boolean) => void;
    handleStartAnalysis: (id: string, name: string) => void;
    handleDeleteItems: () => void;
    handleUpdateDocument: (payload: Partial<Document>, documentId?: string, type?: 'file' | 'folder') => void;
    handleActiveMultipleDocuments: (payload: { active: boolean }, documentIds: string[]) => void;
    handleFilterDocuments: (filters: Record<string, string>) => void;
    clearFilterDocuments: () => void;
    handleIsUploadingFile: (isUploading: boolean) => void;
};

type KnowledgeDialogsType = {
    dialogs: {
        isRenameDialogOpen: boolean;
        isMoveDialogOpen: boolean;
        isUploadModalOpen: boolean;
        isPreviewModalOpen: boolean;
        isDeleteDialogOpen: boolean;
        isReplaceDialogOpen: boolean;
    };
    inProgressUploadFiles: Record<string, number> | null;
    progressByEvents: Record<string, number> | null;
    statusUploadFiles: Record<string, uploadRequestStatusType> | null;
    clearUploadProgress: () => void;
    clearUploadStatus: () => void;
};

type KnowledgeViewStatesType = {
    viewType: ViewType;
    currentPath: string;
    selectedItems: string[];
    selectedItemsToDelete: string[];
    selectedItemToRename: FileItem | null;
    selectedItemToMove: FileItem | null;
    selectedMultipleItemsToMove: FileItem[];
    selectedItemsToActive: string[];
    selectedItemToReplace: FileItem | null;
    handleSelectItems: (items: string, selected: boolean) => void;
    handleSelectAllItems: (selected: boolean) => void;
    handleSelectItemsToDelete: (items: string[]) => void;
    handleSelectItemToRename: (item: FileItem) => void;
    handleSelectItemToMove: (item: FileItem) => void;
    handleSelectMultipleItemToMove: (item: FileItem[]) => void;
    handleSelectItemToActive: (item: FileItem) => void;
    handleSelectItemToReplace: (item: FileItem) => void;
    clearSelectedItems: () => void;
    clearSelectItemsToDelete: () => void;
    clearSelecteItemToRename: () => void;
    clearSelecteItemToMove: () => void;
    clearSelectItemToReplace: () => void;
};

type KnowledgeDragginStatesType = {
    previewFile: FileItem | null; // archivo que se esta previsualizando
    uploadingFiles: File[]; // archivos que se estan subiendo
    isDragging: boolean; // estado de arrastre, cuando se arrastra un elelemento desde fuera de la app
    internalDrag: boolean; // estado de arrastre, cuando se arrastra un elelemento desde dentro de la app
    draggedFileId: string | null; // identificador de archivo dentro de la aplicacion arrastrado
    handleDrag: (isDragging: boolean) => void; // funcion para manejar el inicio del arrastre
    handleDragInternal: (isDragging: boolean, id: string | null) => void; // funcion para manejar el arrastre interno
    handleUploadFiles: (files: File[], destination?: UploadDestination) => void; // funcion para manejar la subida de archivos
    handlePreviewFile: (file: FileItem | null) => void; // funcion para manejar la previsualizacion de archivos
};

type KnowledgeContextType = KnowledgeCRUDActionsType &
    KnowledgeViewStatesType &
    KnowledgeDialogsType &
    KnowledgeDragginStatesType;

type NonHandlersKnowledgeContextType = Omit<
    KnowledgeContextType,
    | 'handleViewType'
    | 'handleCurrentPath'
    | 'fetchNextPage'
    | 'handleDialogState'
    | 'handleStartAnalysis'
    | 'handleSelectItems'
    | 'handleSelectItemsToDelete'
    | 'handleSelectAllItems'
    | 'clearSelectedItems'
    | 'clearSelectItemsToDelete'
    | 'handleDrag'
    | 'handleDragInternal'
    | 'handleUploadFiles'
    | 'handleDeleteItems'
    | 'clearUploadProgress'
    | 'clearUploadStatus'
    | 'clearRejectedUploadByFileName'
    | 'handleUpdateDocument'
    | 'handleSelectItemToRename'
    | 'handleSelectItemToMove'
    | 'clearSelecteItemToRename'
    | 'clearSelecteItemToMove'
    | 'handleActiveMultipleDocuments'
    | 'handleFilterDocuments'
    | 'clearFilterDocuments'
    | 'handlePreviewFile'
    | 'handleSelectItemToActive'
    | 'handleSelectMultipleItemToMove'
    | 'handleSelectItemToReplace'
    | 'clearSelectItemToReplace'
    | 'handleIsUploadingFile'
>;

const KnowledgeContext = createContext<KnowledgeContextType | undefined>(undefined);

const knowledgeViewDefaultState = {
    viewType: 'table' as ViewType,
    currentPath: '/',
    selectedItems: [],
    selectedItemsToDelete: [],
    selectedItemsToActive: [],
    selectedItemToRename: null as FileItem | null,
    selectedItemToMove: null as FileItem | null,
    selectedMultipleItemsToMove: [] as FileItem[],
    selectedItemToReplace: null as FileItem | null,
};

const knowledgeDialogsDefaultState = {
    dialogs: {
        isRenameDialogOpen: false,
        isMoveDialogOpen: false,
        isUploadModalOpen: false,
        isPreviewModalOpen: false,
        isDeleteDialogOpen: false,
        isReplaceDialogOpen: false,
    },
    inProgressUploadFiles: null,
    statusUploadFiles: null,
    progressByEvents: null,
};

const knowledgeCRUDDefaultState = {
    pages: [] as FileItem[],
    statictics: {
        folders: 0,
        files: {
            total: 0,
        },
    },
    paths: [] as { id: string; name: string }[],
    hasNextPage: false,
    isLoadingDocuments: false,
    isFetchingDocuments: false,
    isLoadingMorePages: false,
    isLoadingDelete: false,
    isErrorLoadingDocuments: false,
    isLoadingUpdateDocument: false,
    isLoadingUploadFile: false,
    documentFilters: undefined,
    rejectedUploads: [],
};

const knowledgeDraggihDefaultState = {
    previewFile: null as FileItem | null,
    uploadingFiles: [] as File[],
    isDragging: false,
    internalDrag: false,
    draggedFileId: null,
};

const defaultKnowledgeContext: NonHandlersKnowledgeContextType = {
    ...knowledgeViewDefaultState,
    ...knowledgeDialogsDefaultState,
    ...knowledgeCRUDDefaultState,
    ...knowledgeDraggihDefaultState,
};

export const KnowledgeProvider: React.FC<React.PropsWithChildren<Record<string, unknown>>> = ({ children }) => {
    const { toast, toasts } = useToast();
    const queryClient = useQueryClient();
    const userInfo: IAuthMeV2OutputDto | undefined = queryClient.getQueryData(['userInfo']);

    const refAuxProgressEvent = useRef<Record<string, number> | null>(null);
    const refAuxStatusEvent = useRef<Record<string, uploadRequestStatusType> | null>(null);
    const refRejectedUploads = useRef<PromiseRejectedResult[]>([]);
    const refGetDocumentCount = useRef(0);
    if (!userInfo?.currentOrganization) {
        console.error('No se ha encontrado la organizacion del usuario');
    }

    // estado inicial del contexto
    // se inicializa el contexto con el valor por defecto
    const [knowledgeContext, setKnowledgeContext] = useState<NonHandlersKnowledgeContextType>({
        ...defaultKnowledgeContext,
        currentPath: userInfo?.currentOrganization ? `/${userInfo.currentOrganization}` : '/',
    });

    /** ***************************************** */
    // carga los documentos
    /** ***************************************** */
    const location = useLocation();
    const ids = useMemo(
        () =>
            location.pathname
                .replace(/^\/knowledge\//, '') // Quitar el prefijo /knowledge/
                .split('/') // Dividir por cada nivel
                .filter(Boolean),
        [location.pathname],
    );

    const documentFilters =
        ids.length === 1 && userInfo?.currentOrganization !== ids[0]
            ? { ...knowledgeContext.documentFilters, folder_id: ids[0] } // Combina filtros + folder_id
            : knowledgeContext.documentFilters;

    const {
        data: documents,
        fetchNextPage,
        hasNextPage,
        isLoading: isLoadingDocuments,
        isFetchingNextPage: isLoadingMorePages,
        isError: isErrorLoadingDocuments,
        isFetching: isFetchingDocuments,
        failureCount,
    } = useGetDocuments(userInfo?.currentOrganization as string, documentFilters, !!userInfo?.currentOrganization);

    const handleFilterDocuments = (filters: Record<string, string>) => {
        setKnowledgeContext((prevState) => ({
            ...prevState,
            documentFilters: filters,
        }));
    };

    const clearFilterDocuments = () => {
        setKnowledgeContext((prevState) => ({
            ...prevState,
            documentFilters: undefined,
        }));
    };

    /** ***************************************** */
    // delete file
    /** ***************************************** */
    const { mutateAsync: deleteDocument, isPending: isLoadingDelete } = useDeleteDocument();

    // flatea  todas la paginas cargadas con el el hook useGetDocuemnts
    const pages = (documents?.pages.flatMap((page) => page.data) as FileItem[]) ?? [];

    const paths = documents?.pages.at(-1)?.meta?.path || [];
    /** ***************************************** */
    // uploadFile
    /** ***************************************** */
    const handleUploadProgress = useCallback((name: string, progress: number) => {
        if (refAuxProgressEvent.current === null) {
            refAuxProgressEvent.current = { [name]: progress };
        } else {
            refAuxProgressEvent.current = { ...refAuxProgressEvent.current, [name]: progress };
        }
        setKnowledgeContext((prevState) => ({ ...prevState }));
    }, []);

    const handleUploadProgressEvent = useCallback(
        (
            event: AxiosProgressEvent & {
                name: string;
            },
        ) => {
            const percentage = Math.floor((event.progress || 0) * 100);
            handleUploadProgress(event.name, percentage);
        },
        [handleUploadProgress],
    );

    const clearUploadProgress = () => {
        refAuxProgressEvent.current = null;
    };

    const clearUploadStatus = () => {
        refAuxStatusEvent.current = null;
    };

    const clearRejectedUploadByFileName = (fileName: string) => {
        refRejectedUploads.current = refRejectedUploads.current.filter((r) => r.reason?._fileName !== fileName);
        if (refAuxStatusEvent.current) {
            const { [fileName]: _, ...rest } = refAuxStatusEvent.current;
            refAuxStatusEvent.current = Object.keys(rest).length > 0 ? rest : null;
        }
        if (refAuxProgressEvent.current) {
            const { [fileName]: _, ...rest } = refAuxProgressEvent.current;
            refAuxProgressEvent.current = Object.keys(rest).length > 0 ? rest : null;
        }
        setKnowledgeContext((prevState) => ({ ...prevState }));
    };

    const { mutateAsync: uploadFile } = useUploadKnoledgeFile();

    const { mutateAsync: updateFolder } = useUpdateFolder();
    const { mutateAsync: deleteFolder } = useDeleteFolder();
    /** ***************************************** */
    // Actualizar documentos hook
    /** ***************************************** */

    const { mutateAsync: updateDocument, isPending: isLoadingUpdateDocument } = useUpdateDocument();

    const handleUpdateDocument = (
        payload: Partial<Document | Folder>,
        documentId?: string,
        type?: 'file' | 'folder',
    ) => {
        const auxId = knowledgeContext.selectedItemToRename?.id || documentId;
        const auxType = knowledgeContext.selectedItemToRename?.type || type;
        if (auxId && auxType === 'file') {
            updateDocument({
                documentId: documentId as string,
                organizationId: userInfo?.currentOrganization as string,
                payload: {
                    ...payload,
                    uploaded_by:
                        payload.uploaded_by && typeof payload.uploaded_by === 'object'
                            ? {
                                  ...payload.uploaded_by,
                                  photo: (payload.uploaded_by as any).photo ?? '',
                              }
                            : payload.uploaded_by,
                },
            })
                .then(() => {
                    queryClient.refetchQueries({ queryKey: ['documents'], exact: true });
                    toast({
                        title: 'Documento actualizado',
                        description: 'El documento se actualizo correctamente',
                        variant: 'default',
                    });
                    queryClient.invalidateQueries({ queryKey: ['documents'] });
                    queryClient.invalidateQueries({ queryKey: ['folderStatistics'] });
                })
                .catch((error) => {
                    toast({
                        title: 'Error al actualizar el documento',
                        description: error instanceof Error ? error.message : 'Error desconocido',
                        variant: 'destructive',
                    });
                })
                .finally(() => {
                    setKnowledgeContext((prevState) => ({
                        ...prevState,
                        selectedItemToRename: null,
                        dialogs: {
                            ...prevState.dialogs,
                            isRenameDialogOpen: false,
                        },
                    }));
                });
        } else if (auxId && auxType === 'folder') {
            updateFolder({
                folderId: auxId,
                organizationId: userInfo?.currentOrganization as string,
                name: payload.name as string,
                parentId: (payload as Partial<Folder>).parent_id as string | undefined,
            })
                .then(() => {
                    queryClient.refetchQueries({ queryKey: ['documents'], exact: true });
                    toast({
                        title: 'Carpeta actualizada',
                        description: 'La carpeta se actualizo correctamente',
                        variant: 'default',
                    });
                    queryClient.invalidateQueries({ queryKey: ['documents'] });
                })
                .catch((error) => {
                    toast({
                        title: 'Error al actualizar la carpeta',
                        description: 'La carpeta no se actualizo correctamente',
                        variant: 'destructive',
                    });
                    console.error('Error al actualizar la carpeta', error);
                })
                .finally(() => {
                    setKnowledgeContext((prevState) => ({
                        ...prevState,
                        selectedItemToRename: null,
                        dialogs: {
                            ...prevState.dialogs,
                            isRenameDialogOpen: false,
                        },
                    }));
                });
        }
    };

    const handleActiveMultipleDocuments = async (payload: { active: boolean }, documentIds: string[]) => {
        const promises = documentIds.map((documentId) => {
            return updateDocument({
                documentId: documentId,
                organizationId: userInfo?.currentOrganization as string,
                payload,
            });
        });
        const results = await Promise.allSettled(promises);
        const fulfilled = results.filter((r) => r.status === 'fulfilled');
        const rejected = results.filter((r) => r.status === 'rejected');

        if (fulfilled.length > 0) {
            toast({
                title: `Documentos ${payload.active ? 'activados' : 'desactivados'}`,
                description: `Se ${payload.active ? 'activaron' : 'desactivaron'} ${fulfilled.length} correctamente`,
                variant: 'default',
            });
            queryClient.refetchQueries({ queryKey: ['documents', 'folderStatistics'], exact: true });
        }
        if (rejected.length > 0) {
            toast({
                title: `Error al ${payload.active ? 'activar' : 'desactivar'} documentos`,
                description: `No se pudieron ${payload.active ? 'activar' : 'desactivar'} ${
                    rejected.length
                } documentos`,
                variant: 'destructive',
            });
            console.error('Error al eliminar archivos', rejected);
        }
    };
    /****************************************************  */
    /* Manejo de estado de elementos visuales de la tabla  */
    /***************************************************** */

    const handleViewType = (viewType: ViewType) => setKnowledgeContext((prevState) => ({ ...prevState, viewType }));

    const handleCurrentPath = (path: string) => {
        setKnowledgeContext((prevState) => ({ ...prevState, currentPath: path }));
    };

    const selectedItems = (item: string) => {
        setKnowledgeContext((prevState) => ({ ...prevState, selectedItems: [...prevState.selectedItems, item] }));
    };

    const deselectItems = (item: string) => {
        setKnowledgeContext((prevState) => ({
            ...prevState,
            selectedItems: prevState.selectedItems.filter((selectedItem) => selectedItem !== item),
        }));
    };

    const handleSelectItems = (item: string, selected: boolean) => {
        if (selected) {
            selectedItems(item);
        } else {
            deselectItems(item);
        }
    };

    const handleSelectAllItems = (selected: boolean) => {
        if (selected) {
            setKnowledgeContext((prevState) => ({ ...prevState, selectedItems: pages.map((item) => item.id) }));
        } else {
            setKnowledgeContext((prevState) => ({ ...prevState, selectedItems: [] }));
        }
    };

    const clearSelectedItems = () => {
        setKnowledgeContext((prevState) => ({ ...prevState, selectedItems: [] }));
    };

    const handleSelectItemsToDelete = (item: string[]) => {
        setKnowledgeContext((prevState) => ({
            ...prevState,
            selectedItemsToDelete: _.xor(prevState.selectedItemsToDelete, item),
        }));
    };

    const clearSelectItemsToDelete = () => {
        setKnowledgeContext((prevState) => ({ ...prevState, selectedItemsToDelete: [] }));
    };

    const handleSelectItemToRename = (item: FileItem) => {
        setKnowledgeContext((prevState) => ({ ...prevState, selectedItemToRename: item }));
    };

    const handleSelectItemToMove = (item: FileItem) => {
        setKnowledgeContext((prevState) => ({ ...prevState, selectedItemToMove: item }));
    };

    const handleSelectMultipleItemToMove = (item: FileItem[]) => {
        setKnowledgeContext((prevState) => ({
            ...prevState,
            selectedMultipleItemsToMove: _.xor(prevState.selectedMultipleItemsToMove, item),
        }));
    };

    const handleSelectItemToActive = (item: FileItem) => {
        setKnowledgeContext((prevState) => ({
            ...prevState,
            selectedItemsToActive: _.xor(prevState.selectedItemsToActive, [item.id]),
        }));
    };

    const clearSelecteItemToRename = () => {
        setKnowledgeContext((prevState) => ({ ...prevState, selectedItemToRename: null }));
    };

    const clearSelecteItemToMove = () => {
        setKnowledgeContext((prevState) => ({
            ...prevState,
            selectedItemToMove: null,
            selectedMultipleItemsToMove: [],
        }));
    };

    const handleSelectItemToReplace = (item: FileItem) => {
        setKnowledgeContext((prevState) => ({ ...prevState, selectedItemToReplace: item }));
    };

    const clearSelectItemToReplace = () => {
        setKnowledgeContext((prevState) => ({ ...prevState, selectedItemToReplace: null, selectedItems: [] }));
    };

    const handleDeleteItems = async () => {
        const deleteIdByType = {
            folder: pages
                .filter((item) => item.type === 'folder')
                .filter((item) => knowledgeContext.selectedItemsToDelete.includes(item.id)),
            file: pages
                .filter((item) => item.type === 'file')
                .filter((item) => knowledgeContext.selectedItemsToDelete.includes(item.id)),
        };

        const promises = [
            ...deleteIdByType.file.map((item) =>
                deleteDocument({ documentId: item.id, organizationId: userInfo?.currentOrganization as string }),
            ),
            ...deleteIdByType.folder.map((item) =>
                deleteFolder({
                    folderId: item.id,
                    organizationId: userInfo?.currentOrganization as string,
                    recursive: true,
                }),
            ),
        ];

        const results = await Promise.allSettled(promises);

        const fulfilled = results.filter((r) => r.status === 'fulfilled');
        const rejected = results.filter((r) => r.status === 'rejected');

        if (fulfilled.length > 0) {
            toast({
                title: 'Archivos eliminados',
                description: `Se han eliminado ${fulfilled.length} archivos`,
                variant: 'default',
            });
            queryClient.invalidateQueries({ queryKey: ['documents'] });
            queryClient.invalidateQueries({ queryKey: ['folderStatistics'] });
        }
        if (rejected.length > 0) {
            toast({
                title: 'Error al eliminar archivos',
                description: `No se han podido eliminar ${rejected.length} archivos`,
                variant: 'destructive',
            });
            console.error('Error al eliminar archivos', rejected);
        }
        const fulfilledIds = new Set(
            fulfilled.map((r) => (r as PromiseFulfilledResult<{ document_id: string }>).value?.document_id),
        );

        setKnowledgeContext((prevState) => ({
            ...prevState,
            dialogs: {
                ...prevState.dialogs,
                isDeleteDialogOpen: false,
            },
            selectedItemsToDelete: prevState.selectedItemsToDelete.filter((item) => !fulfilledIds.has(item)),
            selectedItems: prevState.selectedItems.filter((item) => !fulfilledIds.has(item)),
        }));
    };

    /****************************************************  */
    /* Manejo de estado de los dialogos */
    /***************************************************** */

    const handleDialogState = (dialog: keyof NonHandlersKnowledgeContextType['dialogs'], isOpen: boolean) => {
        setKnowledgeContext((prevState) => ({
            ...prevState,
            dialogs: {
                ...prevState.dialogs,
                [dialog]: isOpen,
            },
        }));
    };

    const handleIsUploadingFile = useCallback((isUploading: boolean) => {
        setKnowledgeContext((prevState) => ({
            ...prevState,
            isLoadingUploadFile: isUploading,
        }));
    }, []);

    const { replaceFileMutation } = useReplaceFile({
        handleDialogState,
        handleIsUploadingFile,
        clearSelectItemToReplace,
    });

    // Iniciar solicitud de analisis de documentos

    const handleStartAnalysis = (id: string, name: string) => {
        toast({
            title: 'Analisis de documentos',
            description: `Se a iniciado la solicitud de analisis de ${name}, esto puedo tomar unos minutos`,
            variant: 'default',
        });
        // aca se inicia la solicitud de analisis de documentos
    };

    const handleUploadStatus = useCallback((name: string, status: uploadRequestStatusType) => {
        if (refAuxStatusEvent.current === null) {
            refAuxStatusEvent.current = { [name]: status };
        } else {
            refAuxStatusEvent.current = { ...refAuxStatusEvent.current, [name]: status };
        }
        setKnowledgeContext((prevState) => ({ ...prevState }));
    }, []);

    const handlePreviewFile = (file: FileItem | null) => {
        setKnowledgeContext((prevState) => ({
            ...prevState,
            previewFile: file,
        }));
    };

    /****************************************************  */
    /* Manejo de drag and drop */
    /***************************************************** */

    const handleDrag = (isDragging: boolean) => {
        setKnowledgeContext((prevState) => ({
            ...prevState,
            isDragging,
        }));
    };

    const handleDragInternal = (isDragging: boolean, id: string | null) => {
        setKnowledgeContext((prevState) => ({
            ...prevState,
            internalDrag: isDragging,
            draggedFileId: id,
        }));
    };

    const calculateProcessingTime = (sizeInBytes: number): number => {
        const sizeInMB = sizeInBytes / (1024 * 1024);

        if (sizeInMB <= 1) return 8 * 1000;

        if (sizeInMB > 1 && sizeInMB <= 4) return 10 * 1000;

        return sizeInMB * 5000; //5 segs por mega
    };

    const handleUploadFiles = useCallback(
        async (files: File[], destination: UploadDestination = UploadDestination.STANDARD) => {
            handleIsUploadingFile(true);
            setKnowledgeContext((prevState) => ({
                ...prevState,
                uploadingFiles: files,
            }));

            try {
                if (userInfo?.currentOrganization && files.length > 0 && knowledgeContext.dialogs.isUploadModalOpen) {
                    const promises = files.map((file) => {
                        return uploadFile({
                            file,
                            organizationId: userInfo?.currentOrganization,
                            handleProgressEvent: handleUploadProgressEvent,
                            parentId: ids[0] === userInfo?.currentOrganization ? undefined : ids[0],
                            destination,
                        })
                            .then((response) => {
                                handleUploadStatus(file.name, 'successful');
                                return response;
                            })
                            .catch((error: any) => {
                                handleUploadStatus(file.name, 'failed');
                                error._fileName = file.name;
                                throw error;
                            });
                    });

                    const results = await Promise.allSettled(promises);
                    const fulfilled = results.filter((r): r is PromiseFulfilledResult<any> => r.status === 'fulfilled');

                    const rejected = results.filter(
                        (r): r is PromiseRejectedResult & { reason?: { name: string } } => r.status === 'rejected',
                    );
                    refRejectedUploads.current = rejected;
                    if (fulfilled.length > 0) {
                        queryClient.invalidateQueries({ queryKey: ['documents'] });
                    }

                    if (rejected.length > 0) {
                        console.error('Error al subir archivos', rejected);
                    }
                    // Poll documentation analysis in background without blocking the UI loading state
                    fulfilled.forEach(async (file) => {
                        try {
                            const data = await agentsApi.getDocumentById(file.value.id);

                            while (data.status === 'analyzing') {
                                await new Promise((resolve) =>
                                    setTimeout(resolve, calculateProcessingTime(file.value.size)),
                                );
                                const updatedData = await agentsApi.getDocumentById(file.value.id);

                                if (updatedData.status !== 'analyzing') {
                                    queryClient.invalidateQueries({ queryKey: ['documents'] });
                                    break;
                                }
                            }
                        } catch (error) {
                            console.error(`Error polling status for file ${file.value.id}:`, error);
                        }
                    });
                } else if (knowledgeContext.dialogs.isReplaceDialogOpen && knowledgeContext.selectedItemToReplace) {
                    replaceFileMutation.mutate({ file: knowledgeContext.selectedItemToReplace, newFile: files[0] });
                } else {
                    toast({
                        title: 'Error al subir archivos',
                        description: 'No se ha podido subir el archivo, no se ha encontrado la organizacion',
                        variant: 'destructive',
                    });
                }
            } catch (error) {
                console.error('Error in handleUploadFiles:', error);
            } finally {
                handleIsUploadingFile(false);
            }
        },
        [
            handleUploadProgressEvent,
            handleUploadStatus,
            queryClient,
            toast,
            uploadFile,
            userInfo?.currentOrganization,
            ids,
            knowledgeContext.dialogs.isUploadModalOpen,
            knowledgeContext.dialogs.isReplaceDialogOpen,
            knowledgeContext.selectedItemToReplace,
            replaceFileMutation,
            handleIsUploadingFile,
        ],
    );

    useEffect(() => {
        if (
            isErrorLoadingDocuments &&
            !toasts.some((toast) => toast.title === 'Error al cargar documentos') &&
            failureCount !== refGetDocumentCount.current
        ) {
            toast({
                title: 'Error al cargar documentos',
                description: 'Hubo un error al cargar los documentos',
                variant: 'destructive',
            });
            refGetDocumentCount.current = failureCount;
        }
    }, [failureCount, isErrorLoadingDocuments, toast, toasts]);

    useEffect(() => {
        return () => {
            if (!knowledgeContext.dialogs.isUploadModalOpen && refRejectedUploads.current.length > 0) {
                refRejectedUploads.current = [];
            }
        };
    });
    const statictics = useMemo(() => {
        if (documents?.pages && documents.pages.length > 0) {
            const meta = documents.pages.at(-1)?.meta;
            return {
                folders: meta?.total_folders || 0,
                files: {
                    total: meta?.total_documents || 0,
                },
            };
        } else {
            return knowledgeCRUDDefaultState.statictics;
        }
    }, [documents]);

    return (
        <KnowledgeContext.Provider
            value={{
                ...knowledgeContext,
                statictics,
                handleViewType,
                handleCurrentPath,
                handleDialogState,
                fetchNextPage: fetchNextPage as unknown as KnowledgeCRUDActionsType['fetchNextPage'],
                handleStartAnalysis,
                handleSelectItems,
                handleSelectItemsToDelete,
                handleSelectAllItems,
                clearSelectedItems,
                clearSelectItemsToDelete,
                handleDragInternal,
                handleDrag,
                handleUploadFiles,
                handleDeleteItems,
                handleUpdateDocument,
                handleSelectItemToRename,
                handleSelectItemToMove,
                handleSelectMultipleItemToMove,
                handleSelectItemToActive,
                handleSelectItemToReplace,
                handleActiveMultipleDocuments,
                clearUploadProgress,
                clearUploadStatus,
                clearRejectedUploadByFileName,
                clearSelecteItemToRename,
                clearSelecteItemToMove,
                clearSelectItemToReplace,
                handleFilterDocuments,
                clearFilterDocuments,
                handlePreviewFile,
                handleIsUploadingFile,
                pages,
                paths,
                hasNextPage,
                isLoadingDocuments,
                isFetchingDocuments,
                isLoadingDelete,
                isLoadingMorePages,
                isLoadingUploadFile: knowledgeContext.isLoadingUploadFile,
                isLoadingUpdateDocument,
                isErrorLoadingDocuments,
                progressByEvents: refAuxProgressEvent.current, // debe ser un ref porque se maneja por eventos asincronos
                statusUploadFiles: refAuxStatusEvent.current, // debe ser un ref porque se maneja por eventos asincronos
                rejectedUploads: refRejectedUploads.current, // debe ser un ref porque se maneja por eventos asincronos
            }}
        >
            {children}
        </KnowledgeContext.Provider>
    );
};

export const useKnowledgeContext = () => {
    const context = useContext(KnowledgeContext);
    if (!context) {
        throw new Error('useKnowledgeContext must be used within a KnowledgeProvider.');
    }

    return context;
};
